/**
 * Design-system primitives.
 *
 * We own the API of every primitive rather than scattering a third-party
 * component's props across hundreds of call sites — that is what makes a
 * later swap of the underlying library a contained change.
 *
 * Accessibility is built in here, once, so feature code cannot forget it:
 * labelled controls, described errors, visible focus, live regions.
 * docs/07-frontend.md §9
 */

import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";

import { useT } from "@/shared/i18n";
import { cn } from "@/shared/lib/cn";

/**
 * Translate a node when — and only when — it is a bare string.
 *
 * The kit is where nearly all user-visible text passes through, so putting the
 * lookup here translates most of the application without every page having to
 * remember to. A node that is already an element was composed by a page out of
 * parts it translated itself, and is returned untouched.
 */
function useTranslated(): (node: ReactNode) => ReactNode {
  const t = useT();
  return (node) => (typeof node === "string" ? t(node) : node);
}

/* -------------------------------------------------------------------------- */
/* Button                                                                      */
/* -------------------------------------------------------------------------- */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--brand)] text-white hover:brightness-110 active:brightness-95 disabled:brightness-100",
  secondary:
    "surface border text-[var(--text-primary)] hover:bg-[var(--surface-muted)]",
  ghost: "text-[var(--text-secondary)] hover:bg-[var(--surface-muted)]",
  danger: "bg-red-600 text-white hover:bg-red-700",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // 44px minimum touch target on md/lg — these portals are used one-handed,
  // in corridors, on phones.
  sm: "h-8 px-3 text-sm",
  md: "h-11 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, className, children, disabled, ...props },
  ref,
) {
  const tr = useTranslated();
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium",
        "transition-[filter,background-color] duration-150",
        "disabled:cursor-not-allowed disabled:opacity-60",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    >
      {loading && <Spinner className="size-4" />}
      {tr(children)}
    </button>
  );
});

/* -------------------------------------------------------------------------- */
/* Translation at the render boundary                                          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/* Field + Input                                                               */
/* -------------------------------------------------------------------------- */

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, required, children }: FieldProps) {
  const t = useT();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {t(label)}
        {required && (
          <span className="ms-1 text-red-600" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="text-muted text-xs">{t(hint)}</p>}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs font-medium text-red-600">
          {t(error)}
        </p>
      )}
    </div>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, id, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      id={id}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid && id ? `${id}-error` : undefined}
      className={cn(
        "surface h-11 w-full rounded-lg border px-3 text-sm",
        "placeholder:text-[var(--text-secondary)]",
        "disabled:cursor-not-allowed disabled:opacity-60",
        invalid && "border-red-500",
        className,
      )}
      {...props}
    />
  );
});

/* -------------------------------------------------------------------------- */
/* Card                                                                        */
/* -------------------------------------------------------------------------- */

interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export function Card({ title, description, actions, className, children }: CardProps) {
  const tr = useTranslated();
  return (
    <section
      className={cn("surface rounded-[var(--radius-card)] border shadow-sm", className)}
    >
      {(title || actions) && (
        <header className="flex items-start justify-between gap-4 border-b border-[var(--border-subtle)] px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="truncate text-sm font-semibold">{tr(title)}</h2>}
            {description && <p className="text-muted mt-0.5 text-xs">{tr(description)}</p>}
          </div>
          {actions}
        </header>
      )}
      {children && <div className="px-5 py-4">{children}</div>}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Alert                                                                       */
/* -------------------------------------------------------------------------- */

type AlertTone = "info" | "success" | "warning" | "danger";

const ALERT_TONES: Record<AlertTone, string> = {
  info: "border-blue-500/30 bg-blue-500/10 text-blue-900 dark:text-blue-100",
  success: "border-green-500/30 bg-green-500/10 text-green-900 dark:text-green-100",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-100",
  danger: "border-red-500/30 bg-red-500/10 text-red-900 dark:text-red-100",
};

interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}

export function Alert({ tone = "info", title, children, className }: AlertProps) {
  const tr = useTranslated();
  return (
    <div
      // Errors are announced to screen readers rather than only shown.
      role={tone === "danger" ? "alert" : "status"}
      className={cn("rounded-lg border px-4 py-3 text-sm", ALERT_TONES[tone], className)}
    >
      {title && <p className="font-semibold">{tr(title)}</p>}
      {children && <div className={cn(title && "mt-1")}>{tr(children)}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Badge                                                                       */
/* -------------------------------------------------------------------------- */

type BadgeTone = "neutral" | "brand" | "success" | "warning" | "danger";

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: "bg-[var(--surface-muted)] text-[var(--text-secondary)]",
  brand: "bg-[var(--brand)]/12 text-[var(--brand)]",
  success: "bg-green-500/15 text-green-700 dark:text-green-300",
  warning: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  danger: "bg-red-500/15 text-red-700 dark:text-red-300",
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        BADGE_TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Spinner / loading states                                                    */
/* -------------------------------------------------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn("size-5 animate-spin", className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex items-center gap-3 px-5 py-10 text-sm" role="status" aria-live="polite">
      <Spinner />
      <span className="text-muted">{t(label)}…</span>
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  const t = useT();
  return (
    <div className="px-5 py-10 text-center">
      <p className="text-sm font-medium">{t(title)}</p>
      {description && <p className="text-muted mt-1 text-xs">{t(description)}</p>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Definition list — the workhorse of every detail screen                      */
/* -------------------------------------------------------------------------- */

export function DataList({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  const id = useId();
  const t = useT();
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map((item) => (
        <div key={`${id}-${item.label}`} className="min-w-0">
          <dt className="text-muted text-xs uppercase tracking-wide">{t(item.label)}</dt>
          <dd className="mt-0.5 truncate text-sm font-medium">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
